<template>
  <div>
    <v-row class="align-center mb-3">
      <v-col cols="12" class="d-flex align-center flex-wrap">
        <div class="mr-auto my-1">
          <gg-card-title class="pt-0">Email</gg-card-title>
        </div>
        <!-- The in-app purchase flow is out of scope for AM-1104, so Buy Email
             links to the public order form. Add Mailbox lives inside each
             account, where the service is already known. -->
        <gg-btn
          class="my-1"
          href="https://www.greengeeks.com/email-hosting/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Buy Email
          <v-icon small class="ml-1">mdi-open-in-new</v-icon>
        </gg-btn>
      </v-col>
    </v-row>

    <gg-card flat>
      <gg-card-text>
        <!-- TableWrapper hides the least important columns as the viewport
             narrows. Sorting and paging are off because addon rows have to stay
             directly under their main domain. Addon rows start collapsed. -->
        <table-wrapper>
          <v-data-table
            :headers="headers"
            :items="rows"
            :loading="isLoading"
            loading-text="Loading your email accounts…"
            item-key="key"
            disable-sort
            disable-pagination
            hide-default-footer
          >
            <template v-slot:item.domain="{ item }">
              <span v-if="item.addon" class="pl-10 text--secondary">
                <v-icon small class="mr-1" aria-hidden="true">mdi-subdirectory-arrow-right</v-icon>{{ item.domain }}
              </span>
              <div v-else class="d-flex align-center">
                <!-- A fixed-width slot either way, so main domains line up
                     whether or not they have addon domains. -->
                <div style="width: 28px;" class="mr-1">
                  <v-btn
                    v-if="item.addonCount"
                    icon
                    small
                    :aria-expanded="String(item.expanded)"
                    :aria-label="`${item.expanded ? 'Hide' : 'Show'} addon domains of ${item.domain}`"
                    @click="toggle(item.serviceId)"
                  >
                    <v-icon small>{{ item.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>
                </div>
                <div>
                  <span class="font-weight-medium">{{ item.domain }}</span>
                  <span v-if="item.addonCount && !item.expanded" class="ml-2 caption text--secondary">
                    +{{ item.addonCount }} addon {{ item.addonCount === 1 ? 'domain' : 'domains' }}
                  </span>
                  <div v-if="item.planLabel" class="caption text--secondary">{{ item.planLabel }}</div>
                </div>
              </div>
            </template>

            <!-- An addon domain is managed as an account of its own (AM-1104,
                 2026-10-07), so its row carries its own counts and button. -->
            <template v-slot:item.mailboxCount="{ item }">
              <span v-if="item.failed" class="text--secondary">Unavailable</span>
              <template v-else>{{ item.mailboxCount }}</template>
            </template>

            <template v-slot:item.storage="{ item }">
              <template v-if="!item.failed">{{ item.storage }}</template>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-end">
                <gg-btn
                  small
                  secondary
                  :to="accountPath(item.domain, 'accounts')"
                  :aria-label="`Mailboxes on ${item.domain}`"
                >
                  Mailboxes
                </gg-btn>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="py-8 text-center">
                <p class="mb-2 font-weight-medium">You don't have any email accounts yet.</p>
                <p class="mb-0">Hosting plans that include email will show up here.</p>
              </div>
            </template>
          </v-data-table>
        </table-wrapper>
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import TableWrapper from '@/components/Common/TableWrapper'
import { accountPath } from '@/consts/email'
import { humanFileSize } from '@/helpers/helpers'

export default {
  name: 'EmailListView',
  components: {
    TableWrapper,
  },
  data () {
    return {
      // serviceIds whose addon domains are shown.
      expanded: [],
      headers: [
        { text: 'Domain', value: 'domain' },
        { text: 'Mailboxes', value: 'mailboxCount' },
        { text: 'Storage', value: 'storage' },
        { text: 'Actions', value: 'actions', align: 'end' },
      ],
    }
  },
  computed: {
    ...mapGetters('emailHub', [
      'emailAccounts',
      'mailboxesStatus',
      'mailboxErrors',
    ]),
    isLoading () {
      return this.mailboxesStatus === 'loading'
    },
    /**
     * Each account's row, followed by one row per addon domain when the
     * customer has expanded that account. Each row counts only its own
     * domain's mailboxes, so the main row leaves out its addons'.
     * `failed` marks a service whose mailboxes could not be read, so its counts
     * are not mistaken for an empty account.
     */
    rows () {
      const failed = this.mailboxErrors.map(error => error.serviceId)

      return this.emailAccounts.reduce((carry, account) => {
        const expanded = this.expanded.includes(account.serviceId)
        const isFailed = failed.includes(account.serviceId)
        carry.push({
          key: `${account.serviceId}:${account.mainDomain}`,
          serviceId: account.serviceId,
          addonCount: account.addonDomains.length,
          expanded,
          domain: account.mainDomain,
          planLabel: account.planLabel,
          mailboxCount: account.mailboxCount,
          storage: humanFileSize(account.storageUsed, false),
          failed: isFailed,
          addon: false,
        })
        if (!expanded) return carry
        account.addons.forEach(addon => {
          carry.push({
            key: `${account.serviceId}:${addon.domain}`,
            domain: addon.domain,
            mailboxCount: addon.mailboxCount,
            storage: humanFileSize(addon.storageUsed, false),
            failed: isFailed,
            addon: true,
          })
        })
        return carry
      }, [])
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadMailboxes', 'loadDomains']),
    ...mapActions(['getServices']),
    accountPath,
    toggle (serviceId) {
      this.expanded = this.expanded.includes(serviceId)
        ? this.expanded.filter(id => id !== serviceId)
        : [...this.expanded, serviceId]
    },
  },
  async created () {
    // Both fan-outs fetch the service list when it is missing, so read it once
    // up front rather than letting them race each other for it.
    if (!this.$store.getters.services || !this.$store.getters.services.length) {
      try {
        await this.getServices()
      } catch (e) {
        console.warn(e)
      }
    }
    this.loadMailboxes()
    // Addon domains only change the nested rows, so a failure here leaves the
    // accounts themselves listed.
    this.loadDomains().catch(e => console.warn(e))
  },
}
</script>
